/**
 * Lightweight logging wrapper for the frontend app.
 *
 * - `debug` / `info` are no-ops in production builds to keep the browser
 *   console clean; they forward to console in dev / test.
 * - `warn` / `error` always forward to the console so genuine problems remain
 *   visible (and surface in error-reporting integrations) in every environment.
 *
 * Prefer this over raw `console.*` in `src/` so logging has a single chokepoint
 * that can later be wired to a remote sink (Sentry, Logflare, etc.).
 */
const isProd = import.meta.env.PROD;

type LogArgs = unknown[];

export const logger = {
  debug: (...args: LogArgs): void => {
    if (!isProd) console.debug(...args);
  },
  info: (...args: LogArgs): void => {
    if (!isProd) console.info(...args);
  },
  log: (...args: LogArgs): void => {
    if (!isProd) console.log(...args);
  },
  warn: (...args: LogArgs): void => {
    console.warn(...args);
  },
  error: (...args: LogArgs): void => {
    console.error(...args);
  },
};

export type Logger = typeof logger;
